/* eslint-disable react/no-array-index-key */
import React, { useCallback, useMemo } from 'react';
import {
  Divider, Grid, IconButton, Tooltip, Typography, Button,
  Alert,
  Stack,
  Box,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useFormState } from 'react-final-form';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InputField from '../../../Components/InputField';

export default function ParticipantPromptGuest({
  attendees, handleDelete, startParticipantNumber, readOnlyAfterCreation, noAttendeeCountChange, participantDescription, attendeeEmailLabel, attributes,
  inviteForm, helperTexts, showTicketCategory, upgradeButtons, upgradeBenefits, customFieldDefinition,
}) {
  const { values } = useFormState();
  const attrByCode = useMemo(
    () => new Map((inviteForm?.attributes || []).map((attr) => [attr.code, attr])),
    [inviteForm?.attributes],
  );

  const customFieldTemplates = useMemo(
    () => (customFieldDefinition || []).map((field) => ({ ...field, baseName: field.name })),
    [customFieldDefinition],
  );

  const attributeFieldTemplates = useMemo(
    () => (attributes || []).reduce((acc, attrCode) => {
      const attr = attrByCode.get(attrCode);
      if (!attr) return acc;
      acc.push({
        baseName: `customAttributes.${attrCode}`,
        label: attr.name,
        type: attr.type,
        options: attr.options?.map(({ label, value }) => ({ name: label, value })),
        required: attr.isRequired,
        gridSize: { xs: 12 },
        helperText: helperTexts?.[attrCode],
      });
      return acc;
    }, []),
    [attributes, attrByCode, helperTexts],
  );

  const emailLabel = attendeeEmailLabel || 'E-Mail (an diese E-Mail wird die Einlasskarte gesendet)';

  const getFields = useCallback((index, attendee, allFieldsDisabled) => {
    if (customFieldTemplates.length) {
      return customFieldTemplates.map((field) => ({
        ...field,
        name: `attendees.${index}.${field.baseName}`,
      }));
    }

    return [
      {
        name: `attendees.${index}.forename`,
        label: 'Vorname',
        required: true,
        gridSize: { xs: 12, md: 6 },
        disabled: attendee?.formOptions?.disabled || allFieldsDisabled,
      },
      {
        name: `attendees.${index}.name`,
        label: 'Nachname',
        required: true,
        gridSize: { xs: 12, md: 6 },
        disabled: attendee?.formOptions?.disabled || allFieldsDisabled,
      },
      {
        name: `attendees.${index}.email`,
        label: emailLabel,
        type: 'email',
        required: true,
        gridSize: { xs: 12, md: 12 },
        disabled: allFieldsDisabled,
        helperText: helperTexts?.email,
      },
      ...attributeFieldTemplates.map((field) => ({
        ...field,
        name: `attendees.${index}.${field.baseName}`,
      })),
    ];
  }, [attributeFieldTemplates, customFieldTemplates, emailLabel, helperTexts?.email]);

  return (
    <>
      {attendees.map((attendee, index) => {
        const allFieldsDisabled = readOnlyAfterCreation && attendee.id;
        const matchingUpgradeButtons = (upgradeButtons || []).filter((b) => parseInt(b.ticketCategoryId, 10) === parseInt(attendee.ticketCategory?.id, 10));
        return (
          // eslint-disable-next-line react/no-array-index-key
          <Grid container key={index} justifyContent="center" alignItems="center">
            <Grid item xs={12}>
              <Typography variant="h6" style={{ marginTop: '20px', marginBottom: '10px' }}>
                {participantDescription || 'Gast'}
                {' '}
                {startParticipantNumber + 1 + index}
                {showTicketCategory ? ` - ${attendee.ticketCategory.name}` : ''}
              </Typography>
            </Grid>
            {matchingUpgradeButtons.length ? (
              <Grid item xs={12}>
                <Alert
                  severity="info"
                  icon={<RocketLaunchIcon fontSize="inherit" />}
                  sx={{ mb: 4, alignItems: 'center' }}
                  action={(
                    <Stack direction="row" spacing={1} sx={{ mr: 2 }}>
                      {matchingUpgradeButtons.map((btn, i) => (
                        <Button
                          key={`${btn.name}-${i}`}
                          href={`${btn.url}?custom=enid-${attendee.id}&email=${encodeURI(attendee.email)}&first_name=${encodeURI(attendee.forename)}&last_name=${encodeURI(attendee.name)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="contained"
                          color="primary"
                          sx={{ ml: 2, textAlign: 'center', fontWeight: 'bold' }}
                        >
                          {btn.name}
                        </Button>
                      ))}
                    </Stack>
                  )}
                >
                  <Box sx={{ ml: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontSize: 18 }}>
                      Hole mehr aus dem Event raus!
                    </Typography>
                    <Box
                      component="ul"
                      sx={{
                        p: 0,
                        m: 0,
                        listStyle: 'none',
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 0.5,
                      }}
                    >
                      {upgradeBenefits.map(({ benefit }, index) => (
                        <Box component="li" key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircleOutlineIcon sx={{ fontSize: 14, color: 'info.main' }} />
                          <Typography variant="caption" sx={{ lineHeight: 1.2, fontSize: 16 }}>
                            {benefit}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Alert>
              </Grid>
            ) : null}
            <Grid item xs={allFieldsDisabled || noAttendeeCountChange ? 12 : 11}>
              <Grid container spacing={2}>
                {getFields(attendee.index, attendee, allFieldsDisabled).map((field) => (
                  <InputField key={field.name} {...field} />
                ))}
              </Grid>
            </Grid>

            {(!allFieldsDisabled && !noAttendeeCountChange) && (
              <Grid item xs={1} sx={{ textAlign: 'center' }}>
                <Tooltip title={`${participantDescription || 'Gast'} löschen`}>
                  <IconButton aria-label="delete" size="large" color="error" onClick={() => handleDelete(attendee.index, null, values)}>
                    <DeleteIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}
            <Grid item xs={12} sx={{ marginTop: '30px' }}>
              <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.5)' }} />
            </Grid>
          </Grid>
        );
      })}
    </>
  );
}
